import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { motion } from "framer-motion";

interface SignInFormProps {
  onMagicLinkClick: () => void;
}

export const SignInForm = ({ onMagicLinkClick }: SignInFormProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    console.log("1. Starting sign in process for:", email);

    try {
      console.log("2. Attempting supabase.auth.signInWithPassword");
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      console.log("3. Sign in response:", { data, error });

      if (error) {
        console.error("4. Sign in error:", error);
        throw error;
      }

      console.log("5. Sign in successful:", data);
      
      // Set the auth header for future requests
      supabase.functions.setAuth(data.session.access_token);
      
      // Fetch profile data to verify RLS
      console.log("6. Fetching profile data");
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();

      console.log("7. Profile fetch response:", { profileData, profileError });

      if (profileError) {
        console.error("8. Profile fetch error:", profileError);
        throw profileError;
      }

      console.log("9. Profile fetch successful:", profileData);
      
    } catch (error: any) {
      console.error("10. Error in sign in process:", error);
      toast({
        title: "Error signing in",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.form 
      initial="hidden"
      animate="visible"
      variants={formVariants}
      onSubmit={handleSubmit} 
      className="space-y-6"
    >
      <motion.div variants={itemVariants} className="space-y-2">
        <Label htmlFor="email">Email address</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="Enter your email"
          className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
        />
      </motion.div>

      <motion.div variants={itemVariants} className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="Enter your password"
            className="pr-10 transition-all duration-200 focus:ring-2 focus:ring-primary/20"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 hover:bg-transparent"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Eye className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </div>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Button
          type="submit"
          className="w-full transition-all duration-200 hover:shadow-lg hover:scale-[1.02]"
          disabled={isLoading}
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isLoading ? "Signing in..." : "Sign in"}
        </Button>
      </motion.div>

      <motion.div variants={itemVariants} className="text-center space-y-4">
        <Link 
          to="/forgot-password" 
          className="text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          Forgot your password?
        </Link>
        
        <div className="block">
          <Link 
            to="/signup" 
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            Don't have an account? Sign up
          </Link>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={onMagicLinkClick}
          className="w-full transition-all duration-200 hover:shadow-md hover:scale-[1.02]"
        >
          Sign in with Magic Link
        </Button>
      </motion.div>
    </motion.form>
  );
};